import { type FC } from "react";
import { Loader2 } from "lucide-react";

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogBody,
} from "@/shared/components/ui/dialog";
import { Button } from "@/shared/components/ui/button";
import { useMobileUploadSession } from "@/shared/hooks/useMobileUploadSession";
import { QrUploadInstructions } from "@/shared/components/QrUploadInstructions";
import { SecurityNote } from "@/shared/components/ui/security-note";

interface MobileUploadModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  onTryAnotherWay?: () => void;
  maxUploads?: number;
}

export const MobileUploadModal: FC<MobileUploadModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  onTryAnotherWay,
  maxUploads,
}) => {
  const { qrSrc, mobileUrl, uploadCount, expiryMinutes } = useMobileUploadSession({
    active: isOpen,
    expiryMinutes: 30,
    maxUploads,
    documentType: "other",
    onUploadReceived: () => {
      onClose();
      onSuccess?.();
    },
  });

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent size="xl">
        <DialogHeader>
          <DialogTitle>Scan the QR code</DialogTitle>
        </DialogHeader>

        <DialogBody>
          {!mobileUrl ? (
            <div className="flex h-[280px] flex-col items-center justify-center">
              <Loader2 className="h-6 w-6 animate-spin text-brand" />
            </div>
          ) : (
            <div className="flex flex-col gap-6">
              <div className="flex items-center gap-6">
                <div className="shrink-0 overflow-hidden rounded-lg border border-primary bg-white pb-3">
                  <div className="flex h-[235px] w-[250px] items-center justify-center overflow-hidden">
                    <img
                      src={qrSrc}
                      alt="QR code to open the mobile upload page"
                      className="h-full w-full object-contain"
                    />
                  </div>
                  <p className="mt-0 px-3 text-center text-sm text-secondary">
                    Link expires in {expiryMinutes} minutes
                  </p>
                </div>

                <QrUploadInstructions uploadCount={uploadCount} />
              </div>

              <SecurityNote text="Documents are saved to your secure vault, where you can find them anytime" />
            </div>
          )}
        </DialogBody>

        {onTryAnotherWay && (
          <div className="flex justify-end">
            <Button variant="outline" onClick={onTryAnotherWay}>
              Try Another Way
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
